import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AccessTimeRounded, AssignmentRounded, ChangeCircleRounded, FactCheckRounded, NotesRounded, PersonRounded } from "@mui/icons-material";
import { getAuditLogs, getDashboardSummary, getVersions } from "../api";
import { C, TABLE, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";

const pageFont = { fontFamily: "inherit" };

const ENTITY_LABELS = {
  all: "All",
  record: "Record management",
  plan: "Schedule history",
  leave: "Leave",
  disruption: "Disruption",
  homework: "Homework",
  blueprint: "Blueprint",
};

const CHANGE_LABELS = {
  "plan.repository.save": "Schedule saved",
  "evidence.submitted": "Evidence submitted",
  "evidence.submitted.by.mentor": "Evidence added by mentor",
  "evidence.review.updated": "Evidence reviewed",
  "evidence.updated": "Evidence updated",
  "evidence.deleted": "Evidence deleted",
  "version.save": "Schedule updated",
  "version.restore": "Rolled back",
  "version.delete": "Version deleted",
  "version.protect": "Protected",
  "version.unprotect": "Protection removed",
  "plan.generate": "Schedule generated",
  "daily.regenerate": "Day regenerated",
  "homework.edit": "Homework edited",
  "leave.add": "Leave added",
  "leave.remove": "Leave removed",
  "disruption.apply": "Disruption applied",
  "blueprint.change": "Blueprint changed",
};

const FRIENDLY_CHANGE_TYPES = {
  "Manual Edit": "Edited",
  "Initial Plan": "Generated",
  "Day Regenerated": "Day regenerated",
  Regenerated: "Regenerated",
  "Schedule saved": "Schedule saved",
  Protected: "Protected",
  Unprotected: "Protection removed",
};

const HIDDEN_ACTIONS = new Set([
  "daily.sync",
  "daily.generate",
  "weekly.save",
  "weekly.restore",
  "weekly.generate",
  "homework.sync",
  "homework.restore",
  "daily.save",
  "daily.restore",
]);

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

function versionFromLog(log) {
  const n = Number(log?.meta?.memberVersion || log?.meta?.version || log?.meta?.newVersion);
  if (Number.isFinite(n) && n >= 1) return `V${n}`;
  const m = String(log?.summary || "").match(/Version\s+(\d+)/i);
  return m ? `V${m[1]}` : "—";
}

function studentFromLog(log) {
  const direct = String(log?.memberName || log?.meta?.memberName || "").trim();
  if (direct && !/^solo$/i.test(direct)) return direct;
  const summary = String(log?.summary || "");
  if (log?.entity === "record") {
    const submitted = summary.match(/^(.+?)\s+(?:submitted|added|updated|deleted|changed)\b/i);
    return submitted?.[1]?.trim() || direct || "Learner";
  }
  const named = summary.match(/^([^·\n]+?)\s*·\s*(?:Version|Day regenerated|Day Regenerated)/i);
  if (named?.[1] && !/^solo$/i.test(named[1].trim())) return named[1].trim();
  return direct || "Solo";
}

function changeLabel(log) {
  const action = String(log.action || "");
  if (action === "daily.regenerate") return "Day regenerated";
  if (action === "version.protect") return "Protected";
  if (action === "version.unprotect") return "Protection removed";
  if (action === "version.restore") return "Rolled back";
  if (action === "version.delete") return "Version deleted";
  if (action === "plan.generate") return "Schedule generated";

  const ct = String(log?.meta?.changeType || "").trim();
  const reason = String(log?.meta?.reason || log?.summary || "");
  if (ct) {
    if (/^manual edit$/i.test(ct) && /regenerat/i.test(reason)) return "Day regenerated";
    if (FRIENDLY_CHANGE_TYPES[ct]) return FRIENDLY_CHANGE_TYPES[ct];
    return ct;
  }
  if (CHANGE_LABELS[action]) return CHANGE_LABELS[action];
  const raw = action.replace(/[._]/g, " ").trim();
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Update";
}

function reasonText(log) {
  const reason = String(log?.meta?.reason || "").trim();
  if (reason) return reason;
  let s = String(log.summary || "").trim();
  s = s.replace(/\s*[·|]\s*(POST|GET|PUT|DELETE|PATCH)\s+\/api\/\S+/gi, "");
  s = s.replace(/\b(POST|GET|PUT|DELETE|PATCH)\s+\/api\/\S+/gi, "");
  s = s.replace(/^(?:[\w\s]+ · )?Version \d+\s*[·—\-]\s*/i, "");
  s = s.replace(/\s*·\s*by\s+\w+/i, "");
  return s || "—";
}

function differenceText(log) {
  const meta = log?.meta || {};
  const action = String(log.action || "");
  const direct = String(meta.differenceSummary || "").trim();
  if (direct && direct !== "—" && !/^no differences?$/i.test(direct)) return direct;

  if (action === "daily.regenerate" && meta.dayId) {
    return `Regenerated day ${meta.dayId}`;
  }
  if (action === "version.protect") return "Version marked protected";
  if (action === "version.unprotect") return "Protection removed from version";
  if (action === "version.delete") return "Version removed from history";
  if (action === "leave.add") return meta.dayId ? `Leave on ${meta.dayId}` : "Leave added to schedule";
  if (action === "leave.remove") return "Leave removed from schedule";
  if (action === "disruption.apply") return "Disruption applied to schedule";
  if (action === "homework.edit") return "Homework content updated";
  if (action === "blueprint.change") return "Blueprint settings updated";
  if (action === "plan.generate" || /initial/i.test(String(meta.changeType || ""))) {
    const rows = Number(meta.rowCount);
    return Number.isFinite(rows) && rows > 0
      ? `New schedule saved (${rows} rows)`
      : "New schedule generated";
  }

  const count = Number(meta.changeCount);
  if (Number.isFinite(count) && count > 0) {
    return `${count} schedule change${count === 1 ? "" : "s"}`;
  }
  if (Number.isFinite(Number(meta.rowCount)) && Number(meta.rowCount) > 0) {
    return `${meta.rowCount} schedule rows`;
  }
  if (meta.dayId) return `Updated ${meta.dayId}`;

  const reason = String(meta.reason || "").trim();
  if (reason) return reason;

  const summary = String(log.summary || "").trim();
  const afterDash = summary.split(/\s*[—–]\s*/).slice(1).join(" — ").trim();
  if (afterDash) return afterDash;

  const change = changeLabel(log);
  return change && change !== "Update" ? change : "No difference details recorded";
}

function changedByEmail(log) {
  const email = String(log?.actorEmail || log?.meta?.changedByEmail || "").trim();
  return email.includes("@") ? email : "";
}

function changedByName(log) {
  return String(log?.actorName || log?.meta?.actorName || "").trim();
}

function recordStatusLabel(value) {
  const status = String(value || "").toLowerCase();
  return ({ pending: "Pending", submitted: "Submitted", reviewed: "Reviewed", approved: "Approved", flagged: "Flagged" })[status] || status || "Not recorded";
}

function changedByFallback() {
  return "—";
}

function dedupeLogs(logs) {
  const versionKeys = new Set();
  const seenIds = new Set();
  for (const log of logs) {
    const v = Number(log?.meta?.version);
    const action = String(log.action || "");
    if (
      Number.isFinite(v) &&
      (action === "version.save" || action === "version.restore" || action === "plan.generate")
    ) {
      versionKeys.add(`${v}|${log.meta?.memberKey || "default"}`);
    }
  }
  return logs.filter((log) => {
    const id = String(log?.id || "");
    if (id && seenIds.has(id)) return false;
    if (id) seenIds.add(id);
    const action = String(log.action || "");
    if (HIDDEN_ACTIONS.has(action)) {
      const v = Number(log?.meta?.version);
      if (Number.isFinite(v) && versionKeys.has(`${v}|${log.meta?.memberKey || "default"}`)) return false;
      if (/^(daily|weekly|homework)\.(save|sync|restore)$/i.test(action)) return false;
    }
    return true;
  });
}

function versionHistoryLogs(versions = [], auditRows = []) {
  const auditedVersions = new Set(auditRows
    .filter((row) => ["version.save", "version.restore", "plan.generate"].includes(String(row.action || "")))
    .map((row) => `${row.meta?.memberKey || "default"}|${row.meta?.memberVersion || row.meta?.version || ""}`));
  return versions
    .filter((version) => {
      const key = `${version.memberKey || "default"}|${version.displayVersion || version.memberVersion || ""}`;
      return !auditedVersions.has(key);
    })
    .map((version) => {
      const generated = /initial|generat/i.test(`${version.changeType || ""} ${version.reason || ""}`);
      const memberVersion = version.displayVersion || version.memberVersion || version.version;
      return {
        id: `version-history-${version.version}`,
        entity: "plan",
        action: generated ? "plan.generate" : "version.save",
        summary: `${version.memberName || "Schedule"} · Version ${memberVersion} · ${version.changeType || "Schedule saved"}${version.reason ? ` — ${version.reason}` : ""}`,
        meta: {
          version: version.version,
          memberVersion,
          memberKey: version.memberKey || "default",
          memberName: version.memberName || "Solo",
          changeType: version.changeType || "Schedule saved",
          reason: version.reason || "",
          differenceSummary: version.differenceSummary || `${version.rowCount || 0} schedule rows saved`,
          rowCount: version.rowCount || 0,
          actorName: version.changedBy || "",
        },
        source: "schedule version history",
        actorName: version.changedBy || "",
        actorEmail: version.changedByEmail || "",
        createdAt: version.createdAt,
      };
    });
}

const thStyle = {
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 700,
  color: "#fff",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "none",
  borderRight: `1px solid ${TABLE.headBorder}`,
  background: TABLE.headBg,
  position: "sticky",
  top: 0,
  zIndex: 1,
  textAlign: "left",
  whiteSpace: "nowrap",
  fontFamily: "inherit",
};

const tdBase = {
  padding: "12px 14px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
  fontSize: 12.5,
  fontFamily: "inherit",
};

export default function MaintainLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [byEntity, setByEntity] = useState({});
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [entity, setEntity] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (ent = entity, mem = memberFilter) => {
    setLoading(true);
    setError("");
    try {
      const res = await getAuditLogs({
        entity: ent === "all" ? undefined : ent,
        memberKey: mem === "all" ? undefined : mem,
        limit: 150,
      });
      if (res.success) {
        let rows = res.logs || [];
        let scheduleHistoryCount = 0;
        let recordTotal = null;
        let recordMembers = [];
        if (ent === "all" || ent === "plan") {
          try {
            const versionsRes = await getVersions(mem !== "all" && ent === "plan" ? { memberKey: mem } : {});
            const historyRows = versionHistoryLogs(versionsRes?.versions || [], rows);
            scheduleHistoryCount = historyRows.length;
            rows = [...rows, ...historyRows];
          } catch (_) { /* keep current audit rows if version history is unavailable */ }
        }
        // Record events are scoped to the learner, while generic audit history
        // is scoped to the signed-in user. Fetch the record lane separately so
        // the All view and its category count include mentor/learner activity.
        if (ent === "all") {
          try {
            const recordRes = await getAuditLogs({ entity: "record", limit: 150 });
            if (recordRes?.success) {
              const nonRecordRows = rows.filter((row) => row.entity !== "record");
              rows = [...nonRecordRows, ...(recordRes.logs || [])];
              recordTotal = Number(recordRes.total ?? recordRes.logs?.length ?? 0);
              recordMembers = Array.isArray(recordRes.members) ? recordRes.members : [];
            }
          } catch (_) { /* keep the other audit categories available */ }
        }
        // The dashboard's established submission feed is ActivityEvent. Keep it
        // visible here as a compatibility source for records created before the
        // AuditLog record category was introduced.
        if (ent === "record" && rows.length === 0) {
          try {
            const dashboard = await getDashboardSummary();
            rows = (dashboard?.recentActivity || []).map((event) => ({
              id: `dashboard-event-${event.id}`,
              entity: "record",
              action: String(event.title || "Record update").toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.|\.$/g, ""),
              summary: event.description || event.title || "Record activity",
              meta: {
                evidenceId: event.evidenceId || "",
                learnerId: event.learnerId || "",
                memberKey: event.learnerId || "default",
                memberName: event.learnerName || "Learner",
                actorName: event.actorName || "",
                activityTitle: event.activityTitle || "",
                status: event.status || "",
                previousStatus: event.previousStatus || "",
                reviewNote: event.reviewNote || "",
              },
              source: "dashboard activity feed",
              actorName: event.actorName || "",
              actorEmail: event.actorEmail || "",
              createdAt: event.createdAt,
              memberName: event.learnerName || "Learner",
              memberKey: event.learnerId || "default",
            }));
            if (mem !== "all") rows = rows.filter((row) => row.memberKey === mem);
          } catch (_) { /* Keep the audit-log result if the summary feed is unavailable. */ }
        }
        const cleanRows = dedupeLogs(rows)
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          .slice(0, 150);
        setLogs(cleanRows);
        const recordVisibleCount = ent === "record"
          ? Math.max(Number(res.total || 0), cleanRows.length)
          : recordTotal;
        setTotal(recordTotal == null
          ? (ent === "record" ? recordVisibleCount : Number(res.total ?? cleanRows.length) + scheduleHistoryCount)
          : Math.max(0, Number(res.total ?? 0) - Number(res.byEntity?.record || 0)) + recordTotal + scheduleHistoryCount);
        setByEntity({
          ...(res.byEntity || {}),
          ...((ent === "all" || ent === "plan") && scheduleHistoryCount ? { plan: Number(res.byEntity?.plan || 0) + scheduleHistoryCount } : {}),
          ...(ent === "record" ? { record: recordVisibleCount } : {}),
          ...(recordTotal == null ? {} : { record: recordTotal }),
        });
        if (recordMembers.length) setMembers(recordMembers);
        else if (Array.isArray(res.members) && res.members.length) setMembers(res.members);
        else if (ent === "record") {
          const roster = new Map();
          cleanRows.forEach((row) => {
            const key = String(row.memberKey || row.meta?.memberKey || "default");
            const previous = roster.get(key) || { memberKey: key, memberName: row.memberName || row.meta?.memberName || "Learner", count: 0 };
            previous.count += 1;
            roster.set(key, previous);
          });
          setMembers([...roster.values()]);
        }
      } else setError(res.error || "Could not load logs");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Could not load logs");
    }
    setLoading(false);
  }, [entity, memberFilter]);

  useEffect(() => { load("all", "all"); }, []);

  const filterChips = ["all", "record", "plan", "leave", "disruption", "homework", "blueprint"];
  const visible = useMemo(() => logs, [logs]);
  const visibleMembers = useMemo(() => members.filter((member) =>
    String(member.memberName || "").toLowerCase().includes(memberSearch.trim().toLowerCase())
  ), [members, memberSearch]);

  return (
    <div style={{ maxWidth: 1440, margin: "0 auto", padding: "8px 4px 32px", ...pageFont }}>
      <PageHeader
        title="Maintain Logs"
        subtitle="Use Schedule history and Record management as separate activity views."
        actions={
          <Btn variant="outline" onClick={() => load(entity, memberFilter)} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Btn>
        }
      />

      <Card style={{ marginBottom: 14 }} sx={{ p: 2 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>
          Filter by area
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: members.length ? 12 : 0 }}>
          {filterChips.map((key) => {
            const count = key === "all" ? visible.length : Number(byEntity[key] || 0);
            const active = entity === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setEntity(key); setMemberFilter("all"); load(key, "all"); }}
                style={{
                  border: `1.5px solid ${active ? C.primary : C.border}`,
                  background: active ? C.primarySoft : C.surface,
                  color: active ? C.primaryDark : C.text,
                  borderRadius: 999,
                  padding: "7px 14px",
                  fontSize: 13,
                  fontWeight: 750,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  letterSpacing: "inherit",
                }}
              >
                {ENTITY_LABELS[key]}
                {key === "all" || key === "record" || count ? ` (${key === "all" ? visible.length : count})` : ""}
              </button>
            );
          })}
        </div>
        {members.length > 0 && entity === "record" && (
          <>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 10 }}>
              Filter by student
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: 10, alignItems: "center", maxWidth: 680 }}>
              <select
                aria-label="Filter record logs by learner"
                value={memberFilter}
                onChange={(event) => { setMemberFilter(event.target.value); load(entity, event.target.value); }}
                style={{ width: "100%", minWidth: 0, boxSizing: "border-box", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", background: C.surface, color: C.text, font: "inherit", fontSize: 13 }}
              >
                <option value="all">All students ({members.length})</option>
                {visibleMembers.map((member) => (
                  <option key={member.memberKey} value={member.memberKey}>
                    {member.memberName || "Learner"}{member.count ? ` · ${member.count} log${member.count === 1 ? "" : "s"}` : ""}
                  </option>
                ))}
              </select>
              <input
                aria-label="Search learners"
                value={memberSearch}
                onChange={(event) => setMemberSearch(event.target.value)}
                placeholder="Search learners"
                style={{ width: "100%", minWidth: 0, boxSizing: "border-box", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", background: C.surface, color: C.text, font: "inherit", fontSize: 13 }}
              />
            </div>
          </>
        )}
      </Card>

      {error && (
        <div style={{
          marginBottom: 12, padding: "10px 14px", borderRadius: LAYOUT.radiusSm,
          background: C.errorBg, color: C.error, fontSize: 13, fontWeight: 600,
          border: `1px solid ${C.error}33`,
        }}>
          {error}
        </div>
      )}

      {loading && visible.length === 0 && (
        <Card><p style={{ margin: 0, color: C.muted, fontSize: 13, fontWeight: 600 }}>Loading…</p></Card>
      )}

      {!loading && visible.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: C.muted, fontSize: 13, fontWeight: 600, textAlign: "center", padding: 8 }}>
            {entity === "record"
              ? "No learner submission or review activity was found for this account scope."
              : "No changes logged yet. Generate or edit a schedule to start the audit trail."}
          </p>
        </Card>
      )}

      {visible.length > 0 && (
        <Card style={{ padding: 0 }} sx={{ p: "0 !important", overflow: "hidden" }}>
          <div style={{ overflowX: "auto", maxHeight: "70vh", overflowY: "auto" }}>
            <table style={{ width: "100%", minWidth: entity === "record" ? 1260 : 1320, tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, ...pageFont }}>
              <colgroup>
                {(entity === "record" ? ["13%", "18%", "15%", "14%", "14%", "11%", "15%"] : entity === "all" ? ["14%", "15%", "11%", "13%", "15%", "15%", "17%"] : ["15%", "16%", "10%", "13%", "15%", "15%", "16%"]).map((width, index) => <col key={index} style={{ width }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><AccessTimeRounded style={{ fontSize: 16 }} />When</span></th>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><PersonRounded style={{ fontSize: 16 }} />{entity === "record" ? "Learner" : entity === "all" ? "Learner / member" : "Student"}</span></th>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><AssignmentRounded style={{ fontSize: 16 }} />{entity === "record" ? "Activity" : entity === "all" ? "Activity / version" : entity === "plan" ? "Version / save" : "Version"}</span></th>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><ChangeCircleRounded style={{ fontSize: 16 }} />Change</span></th>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><PersonRounded style={{ fontSize: 16 }} />{entity === "record" ? "Reviewed by" : entity === "all" ? "Changed by" : "Email"}</span></th>
                  <th style={thStyle}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><FactCheckRounded style={{ fontSize: 16 }} />{entity === "record" ? "Status" : entity === "all" ? "Status / difference" : "Difference"}</span></th>
                  <th style={{ ...thStyle, borderRight: "none" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}><NotesRounded style={{ fontSize: 16 }} />{entity === "record" ? "Review note / details" : "Reason"}</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((log, idx) => {
                  const email = changedByEmail(log);
                  const actorName = changedByName(log);
                  const isRecordLog = entity === "record" || log.entity === "record";
                  const diff = isRecordLog
                    ? (log.meta?.status ? `${log.meta?.previousStatus ? `${recordStatusLabel(log.meta.previousStatus)} → ` : ""}${recordStatusLabel(log.meta.status)}` : "Status not recorded")
                    : differenceText(log);
                  const reason = isRecordLog ? String(log.meta?.reviewNote || log.summary || "—") : reasonText(log);
                  const change = changeLabel(log);
                  const student = studentFromLog(log);
                  const rowBg = idx % 2 === 0 ? C.surface : C.surfaceAlt;

                  return (
                    <tr key={log.id || `${log.createdAt}-${idx}`} style={{ background: rowBg }}>
                      <td style={{ ...tdBase, color: C.muted, whiteSpace: "nowrap", fontWeight: 600 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><AccessTimeRounded style={{ color: C.primary, fontSize: 15 }} />{formatWhen(log.createdAt)}</span>
                      </td>
                      <td
                        style={{
                          ...tdBase,
                          fontWeight: 750,
                          color: C.text,
                          whiteSpace: "normal",
                          wordBreak: "keep-all",
                          overflowWrap: "break-word",
                          lineHeight: 1.35,
                          hyphens: "none",
                        }}
                        title={student}
                      >
                        {isRecordLog ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><span style={{ width: 28, height: 28, flex: "0 0 28px", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 999, color: C.primary, background: C.primarySoft }}><PersonRounded style={{ fontSize: 17 }} /></span><span>{student}</span></span> : student}
                      </td>
                      <td style={{ ...tdBase, fontWeight: 800, color: C.primary, whiteSpace: isRecordLog ? "normal" : "nowrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>{isRecordLog && <AssignmentRounded style={{ color: C.primary, fontSize: 16, flex: "0 0 auto" }} />}{isRecordLog ? log.meta?.activityTitle || "—" : log.action === "plan.repository.save" ? "Archive saved" : versionFromLog(log)}</span>
                      </td>
                      <td style={{ ...tdBase, fontWeight: 700, color: C.text, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "normal", lineHeight: 1.35 }} title={change}>
                        {isRecordLog ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", borderRadius: 999, background: C.primarySoft, color: C.primaryDark, fontSize: 11.5, fontWeight: 750, whiteSpace: "nowrap" }}><ChangeCircleRounded style={{ fontSize: 15 }} />{change}</span> : change}
                      </td>
                      <td style={{ ...tdBase, overflow: "hidden" }}>
                        {isRecordLog && actorName && <div style={{ color: C.text, fontWeight: 750, marginBottom: 3, overflowWrap: "anywhere" }}>{actorName}</div>}
                        {email ? <a href={"mailto:" + email} title={"Email " + email} onClick={(e) => e.stopPropagation()} style={{ color: C.primary, fontWeight: 650, overflowWrap: "anywhere", fontFamily: "inherit", fontSize: 11.5, textDecoration: "none" }}>{email}</a> : actorName && isRecordLog ? null : <span style={{ fontWeight: 650, color: C.faint }}>{changedByFallback()}</span>}
                      </td>
                      <td style={{ ...tdBase, color: C.text, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "normal", lineHeight: 1.4, fontWeight: 600 }} title={diff}>
                        {isRecordLog ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", borderRadius: 999, background: /approved/i.test(diff) ? "#E8F5EE" : /flagged/i.test(diff) ? "#FDECEC" : /pending|submitted/i.test(diff) ? "#FFF4DF" : "#EEF2F7", color: /approved/i.test(diff) ? "#18794E" : /flagged/i.test(diff) ? "#B42318" : /pending|submitted/i.test(diff) ? "#A15C00" : "#526175", fontSize: 11.5, fontWeight: 750, whiteSpace: "nowrap" }}><FactCheckRounded style={{ fontSize: 15 }} />{diff}</span> : diff}
                      </td>
                      <td style={{ ...tdBase, color: C.muted, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "normal", lineHeight: 1.4, fontWeight: 600, borderRight: "none" }} title={reason}>
                        <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 7, minWidth: 0, maxWidth: "100%" }}><NotesRounded style={{ color: C.primary, fontSize: 16, marginTop: 1, flex: "0 0 auto" }} /><span style={{ minWidth: 0, overflowWrap: "anywhere", whiteSpace: "normal" }}>{reason}</span></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{
            padding: "10px 14px",
            borderTop: `1px solid ${C.border}`,
            fontSize: 12,
            color: C.muted,
            fontWeight: 600,
            background: C.surfaceAlt,
          }}>
            {visible.length} change{visible.length === 1 ? "" : "s"}
            {total > visible.length ? ` · filtered from ${total} raw rows` : ""}
            {" · "}account-scoped (other logins cannot see these)
          </div>
        </Card>
      )}
    </div>
  );
}
